import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import { REV_TYPE } from '../../../constants';
import { tapAndSaaSIsolationLicense, tapIsolationLicense } from '../../../productLicense';
import ConsoleStore from '../../../stores/ConsoleStore';
import { USER_GROUPS_SOURCES } from '../../../stores/Policy/BrowsingRoles/BrowsingRolesStore';
import PolicyStore from '../../../stores/Policy/PolicyStore';
import ConcurrencyPopUp from '../../ConcurrencyPopUp/ConcurrencyPopUp';
import { FuseConsoleCardFooter } from '../../ConsoleFragments';
import { FTBody, Page, Card, FTHeading } from '../../Fuse';
import FuseAlert from '../../FuseControls/FuseAlert';
import StackL from '../../Spacers/Spacers';
import { TStrong } from '../../Text/Text';
import PolicyTable from '../PolicyTable';
import PriorityModal from '../PriorityModal';
import UserGroupsSources from '../UserGroupsSources';
import BrowsingRoleModal from './BrowsingRoleModal';
import BrowsingRoleName from './BrowsingRoleName';
import { ROLE_READ_ONLY } from '../../../stores/Administrators/AdministratorModalStore';
import './BrowsingRoles.css';

const block = b.lock('BrowsingRoles');

class BrowsingRoles extends Component {
    static propTypes = {
        console: ConsoleStore.PropType,
        policy: PolicyStore.PropType.isRequired,
    };

    updateUserGroupSources() {
        const { console, policy } = this.props;
        const { browsingRoles } = policy;
        browsingRoles.setAllowedUserGroupsSources({
            [USER_GROUPS_SOURCES.NONE]: true,
            [USER_GROUPS_SOURCES.SAML]: console.isSSOEnabled,
            [USER_GROUPS_SOURCES.TAP]: console.urlIsolationProductEnabled || [
                tapIsolationLicense,
                tapAndSaaSIsolationLicense,
            ].indexOf(console.license) > -1,
        });
    }

    componentDidMount() {
        const { console, policy } = this.props;
        const { browsingRoles } = policy;

        this.updateUserGroupSources();

        browsingRoles.load().then(({ ssoStatus }) => {
            console.setSSOStatus(ssoStatus);
            this.updateUserGroupSources();
        });

        browsingRoles.policyTable.policyModal.sections.userGroups.load();
    }

    searchFilter = (term) => (arr) =>
        (term === '' ? arr : arr.filter((entity) => {
            const check = (entry) => entry.toLowerCase().indexOf(term.toLowerCase()) > -1;
            return check(entity.name) ||
                (entity.effectsUserGroups || []).some(({ name }) => check(name)) ||
                (entity.effectsEmails || []).some(check);
        }))

    tableSubheader = () => <FuseAlert
        type="success"
        icon="info"
        className={block('SubHeader')}
    >
        Priority determines the user&apos;s browser registration and global options only. All
        Roles selected in match criteria for Content and Exclusion Rules are evaluated.
    </FuseAlert>

    renderTable = (priorityMode) => <PolicyTable
        changePriorityAction
        store={this.props.policy.browsingRoles.policyTable}
        priorityMode={priorityMode}
        searchPlaceholder="Search by Name, User Group or Email"
        itemName="Role"
        itemNameFull="Browsing Role"
        readOnly={this.props.console.authority === ROLE_READ_ONLY.value}
        columnsConfig={this.columnsConfig}
        subHeader={this.tableSubheader()}
        noItemsText={null}
        loadFailedMessage="Failed to load roles."
        prioritiesChangeFailedMessage="Failed to change roles priorities."
        searchFilter={this.searchFilter}
        renderName={({ isDefault, name }) => <BrowsingRoleName isDefault={isDefault} name={name} />}
    />;

    renderAboutSection = () => {
        return <Card title="What are Browsing Roles?">
            <FTBody>
                Browsing Roles are sets of users mapped to specific Isolation options.
            </FTBody>
            <StackL />
            <FTHeading>How does the System Use a Browsing Role?</FTHeading>
            <StackL />
            <FTBody>
                When a Browser/Email Isolation user first attempts to register an Isolation Browser,
                the system uses the highest-ranking Browsing Role that contains the user’s email.
                This Browsing Role determines the registration options.
            </FTBody>
            <StackL />
            <FTBody>
                A user’s Browsing Role also provides them with specified Isolation Options when viewing Isolated pages.
            </FTBody>
            <StackL />
            <FTBody>
                Stack-ranking controls are provided in the console so you can prioritize Browsing Roles.
            </FTBody>
            <StackL />
            <FTBody>
                Policies are only operational for enabled Browsing Roles.
            </FTBody>
            <StackL />
            <FTBody>
                All Browsing Roles a user is associated with are checked
                against Content Rule and Exclusion Rule Match Criteria.
            </FTBody>
            <StackL />
            <FTHeading>How to Use Browsing Rules</FTHeading>
            <StackL />
            <FTBody><TStrong>Step 1. Edit the Default Browsing Role</TStrong></FTBody>
            <StackL />
            <FTBody>
                This establishes the system-default options for all users.
                The Default Browsing Role includes all users, is always enabled,
                and always operates at the lowest priority.
            </FTBody>
            <StackL />
            <FTBody><TStrong>Step 2. Select the User Group Source</TStrong></FTBody>
            <StackL />
            <FTBody>
                This optional step synchronizes your User Groups into Console.
                After your groups synchronize, they can be used to assign users to Browsing Roles.
            </FTBody>
            <StackL />
            <FTBody><TStrong>Step 3. Add new Browsing Roles (Optional)</TStrong></FTBody>
            <StackL />
            <FTBody>
                Name the Role, assign its users, and define their options.
                You can now use the newly added Browsing Role to create Rules for specific users.
                This is how you create exceptions to the Default Browsing Role / Default Content Rule.
            </FTBody>
            <StackL />
            <FTHeading>Common Use Case</FTHeading>
            <StackL />
            <FTBody>
                To provide a Power User with additional access to specific sites in Isolation,
                create a Role for those users. Then create a Content Rule that gives that Role broad access,
                such as allowing all content categories.
            </FTBody>
            <StackL />
            <FuseConsoleCardFooter
                text="If no Browsing Roles are added, the system enforces
                      the default Browsing Role for all Browser/Email Isolation users."
            />
        </Card>;
    }

    render() {
        const { policyTable } = this.props.policy.browsingRoles;
        return <Fragment>
            <Page title="Policies - Browsing Roles">
                {this.renderTable()}
                <UserGroupsSources />
                {this.renderAboutSection()}
            </Page>

            <BrowsingRoleModal />

            <PriorityModal
                store={policyTable}
                subTitle={' - Browsing Roles'}
                errMsg={'Failed to change roles priorities'}
            >
                {this.renderTable(true)}
            </PriorityModal>

            <ConcurrencyPopUp
                store={policyTable.concurrencyPopUpStore}
                revisionType={REV_TYPE.BROWSING_ROLE}
            />
        </Fragment>;
    }
}

export default inject('policy', 'console')(observer(BrowsingRoles));
